import {
  PROFILE_DOCUMENT_KIND_HELP,
  PROFILE_DOCUMENT_KIND_LABELS,
  PROFILE_DOCUMENT_KINDS,
  PROFILE_DOCUMENT_TYPE_LABELS,
  PROFILE_DOCUMENT_TYPES,
  formatNumber,
  type ProfileDocumentDto,
  type ProfileDocumentKind,
  type ProfileDocumentType,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, ExternalLink, FileText, Link2, Paperclip, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '../../components/Button.js';
import { GlassPanel } from '../../components/GlassPanel.js';
import { api, query } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import type { Level } from './BrandAdmin.js';

const KIND_ICONS: Record<ProfileDocumentKind, typeof Link2> = {
  LINK: Link2,
  FILE: Paperclip,
  INLINE: FileText,
};

interface Upload {
  fileKey: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  content: string | null;
}

/**
 * Profile material for one level: brand manuals, roadmaps, change logs.
 *
 * The screen is honest about what Nova can and cannot read. A SharePoint link is
 * a pointer, not content — until the Graph integration lands in phase 3, marking
 * one «agentene skal lese» gives them the title and the note, nothing more, and
 * the UI says so rather than letting people assume otherwise.
 */
export function DocumentList({ level }: { level: Level }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const params = { scope: level.scope, company: level.company, areaId: level.areaId };

  const { data: documents = [] } = useQuery({
    queryKey: ['brand-documents', level.scope, level.company, level.areaId],
    queryFn: () => api.get<ProfileDocumentDto[]>(`/brand/documents${query(params)}`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['brand-documents'] });
    void queryClient.invalidateQueries({ queryKey: ['brand-resolved'] });
  };

  const toggleRead = useMutation({
    mutationFn: ({ id, agentsShouldRead }: { id: string; agentsShouldRead: boolean }) =>
      api.patch(`/brand/documents/${id}`, { agentsShouldRead }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/brand/documents/${id}`),
    onSuccess: invalidate,
  });

  return (
    <GlassPanel>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookOpen size={14} className="text-ink-soft" aria-hidden />
          <h2 className="font-display text-ink text-base">Profilmateriell</h2>
          <span className="text-ink-soft text-xs">{formatNumber(documents.length)}</span>
        </div>
        <Button size="sm" icon={<Plus size={13} aria-hidden />} onClick={() => setAdding(true)}>
          Legg til
        </Button>
      </div>

      <p className="text-ink-soft mt-1 text-[11px]">
        Merkevaremanualer, veikart og endringslogger. Det som allerede ligger i SharePoint bør
        lenkes — ikke kopieres.
      </p>

      {documents.length === 0 && !adding && (
        <p className="text-ink-soft/70 mt-4 text-xs">Ingenting lagt til på dette nivået ennå.</p>
      )}

      <ul className="mt-3 space-y-2">
        {documents.map((doc) => {
          const Icon = KIND_ICONS[doc.kind];
          return (
            <li key={doc.id} className="border-hairline bg-base/40 rounded-lg border px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-ink flex items-center gap-1.5 text-xs">
                    <Icon size={12} className="text-ink-soft shrink-0" aria-hidden />
                    <span className="truncate">{doc.title}</span>
                  </p>
                  <p className="text-ink-soft/70 mt-0.5 text-[11px]">
                    {PROFILE_DOCUMENT_TYPE_LABELS[doc.docType]} ·{' '}
                    {PROFILE_DOCUMENT_KIND_LABELS[doc.kind]}
                    {doc.fileSize !== null && ` · ${Math.round(doc.fileSize / 1024)} kB`}
                  </p>
                  {doc.note && <p className="text-ink-soft mt-1.5 text-[11px]">{doc.note}</p>}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  {doc.url && (
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      title="Åpne"
                      aria-label={`Åpne ${doc.title}`}
                      className="text-ink-soft hover:text-accent rounded p-1.5 transition"
                    >
                      <ExternalLink size={13} aria-hidden />
                    </a>
                  )}
                  {doc.kind === 'FILE' && (
                    <a
                      href={`/api/v1/brand/documents/${doc.id}/file`}
                      title="Last ned"
                      aria-label={`Last ned ${doc.title}`}
                      className="text-ink-soft hover:text-accent rounded p-1.5 transition"
                    >
                      <ExternalLink size={13} aria-hidden />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => remove.mutate(doc.id)}
                    title="Fjern"
                    aria-label={`Fjern ${doc.title}`}
                    className="text-ink-soft hover:text-danger rounded p-1.5 transition"
                  >
                    <Trash2 size={13} aria-hidden />
                  </button>
                </div>
              </div>

              <label className="mt-2 flex items-start gap-2 text-[11px]">
                <input
                  type="checkbox"
                  checked={doc.agentsShouldRead}
                  onChange={(e) =>
                    toggleRead.mutate({ id: doc.id, agentsShouldRead: e.target.checked })
                  }
                  className="accent-accent mt-0.5"
                />
                <span className={cn(doc.agentsShouldRead ? 'text-ink' : 'text-ink-soft')}>
                  Agentene skal bruke denne
                  {doc.agentsShouldRead && !doc.readableByAgents && (
                    <span className="text-warning block">
                      Nova kan ikke lese innholdet — agentene får bare tittel, type og notat.
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {adding && (
        <AddDocument level={level} onClose={() => setAdding(false)} onAdded={invalidate} />
      )}
    </GlassPanel>
  );
}

function AddDocument({
  level,
  onClose,
  onAdded,
}: {
  level: Level;
  onClose: () => void;
  onAdded: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<ProfileDocumentKind>('LINK');
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState<ProfileDocumentType>('ANNET');
  const [url, setUrl] = useState('');
  const [content, setContent] = useState('');
  const [note, setNote] = useState('');
  const [agentsShouldRead, setAgentsShouldRead] = useState(false);
  const [upload, setUpload] = useState<Upload | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post('/brand/documents', {
        scope: level.scope,
        company: level.company,
        areaId: level.areaId,
        title,
        docType,
        kind,
        url: kind === 'LINK' ? url : null,
        content: kind === 'INLINE' ? content : (upload?.content ?? null),
        note: note || null,
        agentsShouldRead,
        ...(kind === 'FILE' && upload
          ? {
              fileKey: upload.fileKey,
              fileName: upload.fileName,
              fileSize: upload.fileSize,
              mimeType: upload.mimeType,
            }
          : {}),
      }),
    onSuccess: () => {
      onAdded();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  async function handleFile(file: File) {
    setUploading(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const stored = await api.postForm<Upload>('/brand/uploads', body);
      setUpload(stored);
      if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="border-hairline bg-panel/60 mt-4 space-y-3 rounded-lg border p-3.5">
      <div className="flex gap-0.5">
        {PROFILE_DOCUMENT_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            aria-pressed={kind === k}
            className={cn(
              'rounded-md px-2.5 py-1 text-[11px] transition',
              kind === k ? 'bg-accent/15 text-accent' : 'text-ink-soft hover:text-ink',
            )}
          >
            {PROFILE_DOCUMENT_KIND_LABELS[k]}
          </button>
        ))}
      </div>

      <p className="text-ink-soft/70 text-[11px] leading-relaxed">
        {PROFILE_DOCUMENT_KIND_HELP[kind]}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label-micro mb-1 block" htmlFor="doc-title">
            Tittel
          </label>
          <input
            id="doc-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="border-hairline bg-base/40 text-ink h-9 w-full rounded-lg border px-2.5 text-xs"
          />
        </div>
        <div>
          <label className="label-micro mb-1 block" htmlFor="doc-type">
            Type
          </label>
          <select
            id="doc-type"
            value={docType}
            onChange={(e) => setDocType(e.target.value as ProfileDocumentType)}
            className="border-hairline bg-base/40 text-ink h-9 w-full rounded-lg border px-2 text-xs"
          >
            {PROFILE_DOCUMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {PROFILE_DOCUMENT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {kind === 'LINK' && (
        <div>
          <label className="label-micro mb-1 block" htmlFor="doc-url">
            Adresse
          </label>
          <input
            id="doc-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://prokom.sharepoint.com/…"
            className="border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 h-9 w-full rounded-lg border px-2.5 text-xs"
          />
        </div>
      )}

      {kind === 'FILE' && (
        <div>
          <input
            ref={fileInput}
            type="file"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          <Button size="sm" loading={uploading} onClick={() => fileInput.current?.click()}>
            {upload ? 'Bytt fil' : 'Velg fil'}
          </Button>
          {upload && (
            <p className="text-ink-soft mt-1.5 text-[11px]">
              {upload.fileName} · {Math.round(upload.fileSize / 1024)} kB
              {upload.content === null && ' · innholdet kan ikke leses av agentene'}
            </p>
          )}
        </div>
      )}

      {kind === 'INLINE' && (
        <div>
          <label className="label-micro mb-1 block" htmlFor="doc-content">
            Tekst
          </label>
          <textarea
            id="doc-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={7}
            spellCheck
            lang="nb"
            className="border-hairline bg-base/40 text-ink w-full resize-y rounded-lg border p-3 text-xs leading-relaxed"
          />
        </div>
      )}

      <div>
        <label className="label-micro mb-1 block" htmlFor="doc-note">
          Notat
        </label>
        <input
          id="doc-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Hva dette er, og når det gjelder"
          className="border-hairline bg-base/40 text-ink placeholder:text-ink-soft/50 h-9 w-full rounded-lg border px-2.5 text-xs"
        />
      </div>

      <label className="flex items-center gap-2 text-[11px]">
        <input
          type="checkbox"
          checked={agentsShouldRead}
          onChange={(e) => setAgentsShouldRead(e.target.checked)}
          className="accent-accent"
        />
        <span className="text-ink-soft">Agentene skal bruke denne</span>
      </label>

      {error && <p className="text-danger text-xs">{error}</p>}

      <div className="flex gap-2">
        <Button
          variant="primary"
          size="sm"
          loading={create.isPending}
          disabled={title.trim().length < 2}
          onClick={() => create.mutate()}
        >
          Legg til
        </Button>
        <Button size="sm" onClick={onClose}>
          Avbryt
        </Button>
      </div>
    </div>
  );
}
